import React, { useState, useMemo } from 'react';
import DialogContent from '@material-ui/core/DialogContent';
import StandardFab from '../../../../../customComponents/button/StandardFab';
import { DialogActions, Divider, Typography } from '@material-ui/core';
import OutlinedStadiumButton from '../../../../../customComponents/button/OutlinedStadiumButton';
import StadiumButton from '../../../../../customComponents/button/StadiumButton';
import { ITransformer } from '../../../../../../@types/model/transformer/transformer';
import TransformerDropdown from '../../../../../customComponents/selector/transformer/TransformerSelector';
import MultiFileUploadProgress from '../../../../../customComponents/file/MultiFileUploadProgress';
import TransformerFunctions from '../../../../../../store/transformer/functions';
import uuid from 'uuid';
import { IUserSession } from '../../../../../../@types/employee';
import { firestore } from 'firebase/app';
import * as firebase from 'firebase';
import moment from 'moment';
import generalFunctions from '../../../../../../store/general/functions';
import { KeyboardDatePicker } from '@material-ui/pickers';
import { DATE_FORMAT_DEFAULT_NO_TIME } from '../../../../../../appConstants';
import TransformerSPUMeterProblemSelector from '../../../../../customComponents/selector/transformer/SPUMeterProblemSelector';
import SPUMeterReadingInput, { ISPUMeterReading } from '../../../../../customComponents/input/transformer/SPUMeterReadingInput';
import TransformerMunicipalityReadingHelper, { ITransformerMunicipalityReading } from '../../../../../../@types/model/transformer/reading/transformerMunicipalityReading';
import ProjectDialog from '../../../../../customComponentV2/dialog/ProjectDialog';
import Tooltip from '@material-ui/core/Tooltip';
import Icon from '@material-ui/core/Icon';

interface IMunicipalityMeterCreateDialogProps {
    session ?: IUserSession | null;
}

const MunicipalityMeterCreateDialog = (props : IMunicipalityMeterCreateDialogProps) : React.ReactElement => {
    const [open, setOpen] = useState<boolean>(false);
    const [transformer, setTransformer] = useState<ITransformer | null>(null);
    const [date, setDate] = useState<moment.Moment>(moment.utc().startOf('day'));
    const [guid, setGuid] = useState<string>('');
    const [readings, setReadings] = useState<Array<ISPUMeterReading>>([]);
    const [conditions, setConditions] = useState<Array<string>>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isUploading, setIsUploading] = useState<boolean>(false);

    const onClick = () : void => {
        setOpen(true);
        setDate(moment.utc().startOf('day'));
        setTransformer(null);
        setGuid(uuid.v4());
        setReadings([]);
        setConditions([]);
        setIsUploading(false);
    };

    const onClose = () : void => {
        setOpen(false);
        setIsUploading(false);
        setIsLoading(false);
    };

    const onSubmit = (event : React.FormEvent) : void => {
        event.preventDefault();
        save();
    };

    const save = async () : Promise<void> => {
        if (!props.session || !transformer) return;
        if (readings.some(n => !Number(n.meterReading))) return;

        const reading : ITransformerMunicipalityReading = {
            id: '',
            guid: guid,
            conditions: conditions,
            transformerRef: transformer.ref.id,
            geom: new firestore.GeoPoint(0, 0),
            isWeb: true,
            transformerMeterNumberReadings: readings.map(n => ({
                imageFileName: n.image ? n.image.name : null,
                imageFileUrl: null,
                itemNumber: n.itemNumber,
                meterNumber: n.meterNumber,
                numberOfDigits: n.numberOfDigits,
                reading: Number(n.meterReading),
                recordingFileName: '',
                usage: n.usage,
                division: transformer.Division,
                group: transformer.EPGroup ?? '',
                rollover: n.rollover,
            })),
            date: date.valueOf(),
            division: transformer.Division,
            group: transformer.EPGroup ?? '',
            subArea: transformer.SubArea,
            createdBy: props.session.firebaseUser.uid,
            createdByName: props.session.firebaseUser.displayName ?? '',
            createdByEmployee: props.session.employee.EmployeeNumber ?? '',
            createdOn: firestore.Timestamp.now().toMillis(),
            updatedBy: props.session.firebaseUser.uid,
            updatedByName: props.session.firebaseUser.displayName ?? '',
            updatedByEmployee: props.session.employee.EmployeeNumber ?? '',
            updatedOn: firestore.Timestamp.now().toMillis(),
        };

        try {
            setIsLoading(true);
            await TransformerMunicipalityReadingHelper.save(reading);
            setIsUploading(true);
        } catch (ex) {
            if (ex instanceof Error) generalFunctions.generalShowErrorSnackbar(ex.message ? ex.message : 'Error Creating Reading');
            setIsLoading(false);
        }
    };

    const onEPNumberChange = (value ?: ITransformer) : void => {
        setTransformer(value ?? null);
        setReadings(value?.TransformerMeterNumbers?.map((meterNumber) => ({
            image: null,
            itemNumber: meterNumber.ItemNumber,
            meterNumber: meterNumber.Number,
            numberOfDigits: meterNumber.NumberOfDigits,
            meterReading: '',
            usage: 0,
            rollover: false,
        })) ?? []);
    };

    const onConditionChange = (values : Array<string>) : void => {
        setConditions(values.slice());
    };

    const onMeterReadingChange = (reading : ISPUMeterReading) : void => {
        const updatedReadings = readings.slice();
        const index = updatedReadings.findIndex(x => x.itemNumber === reading.itemNumber);
        updatedReadings.splice(index, 1, { ...reading });
        setReadings(updatedReadings);
    };

    const onFileUpload = (file : File, uploadGuid : string) : firebase.storage.UploadTask | null => {
        if (!transformer) return null;
        const reading = readings.find(x => x.image?.name === file.name);
        if (!reading) return null;
        return TransformerFunctions.uploadMunicipalityReadingFile(transformer.ref.id, file, uploadGuid, reading.itemNumber);
    };

    const onDateChange = (newDate : moment.Moment | null) : void => {
        if (!newDate) return;
        setDate(newDate.startOf('day').utc());
    };

    const onDone = () : void => {
        onClose();
    };

    const files = useMemo<Array<File>>(() => {
        const result : Array<File> = [];
        readings.forEach(n => {
            if (n.image) result.push(n.image);
        });
        return result;
    }, [readings]);

    return (
        <>
            <Tooltip title='Create'>
                <div>
                    <StandardFab color='primary' onClick={onClick} aria-label='tooltip'>
                        <Icon>add</Icon>
                    </StandardFab>
                </div>
            </Tooltip>
            <ProjectDialog
                title='Create Municipality Reading'
                isOpen={open}
                isLoadingCircular={isLoading || isUploading}
                onClose={onClose}
                maxWidth={'md'}
                fullWidth
            >
                <form onSubmit={onSubmit}>
                    {
                        isUploading &&
                        <MultiFileUploadProgress files={files} onFileUpload={(file) => onFileUpload(file, guid)} onDone={onDone} />
                    }
                    <DialogContent className='fdc flx1 hfill bcg0'>
                        {
                            <div className='fdr'>
                                <div className='fdc flx1 pr5 w240'>
                                    <TransformerDropdown
                                        value={transformer?.EPNumber}
                                        label='Select Transformer'
                                        type='municipality'
                                        fullWidth
                                        onChange={onEPNumberChange}
                                        required
                                        activeOnly
                                    />
                                </div>
                                <div className='fdc flx1 pr5 w240'>
                                    <KeyboardDatePicker
                                        value={date}
                                        label='Date'
                                        type='municipality'
                                        format={DATE_FORMAT_DEFAULT_NO_TIME}
                                        fullWidth
                                        onChange={onDateChange}
                                        required
                                    />
                                </div>
                            </div>
                        }
                        {
                            <div className='fdr ais mt15'>
                                <div className='fdc flx1 ais pr5'>
                                    <div className='fdc mb10'>
                                        <Typography className='fs16 lh37 cg3'>
                                            Readings
                                        </Typography>
                                    </div>
                                    <div className='fdc'>
                                        {
                                            readings.map((n) => (
                                                <SPUMeterReadingInput
                                                    readingGuid={guid}
                                                    key={n.itemNumber}
                                                    value={n}
                                                    onChange={onMeterReadingChange}
                                                />
                                            ))
                                        }
                                    </div>
                                    <Divider />
                                </div>
                            </div>
                        }
                        {
                            <div className='fdr ais mt15'>
                                <div className='fdc flx1 ais pr5'>
                                    <div className='fdc mb10'>
                                        <Typography className='fs16 lh37 cg3'>
                                            Problems
                                        </Typography>
                                    </div>
                                    <div className='fdr ais mb10'>
                                        <TransformerSPUMeterProblemSelector values={conditions} onChange={onConditionChange} />
                                    </div>
                                    <Divider />
                                </div>
                            </div>
                        }                        
                    </DialogContent>
                    <DialogActions className='bcg0'>
                        <OutlinedStadiumButton disabled={isLoading || isUploading} className='mr16' variant='text' type='reset' onClick={onClose}>
                            CANCEL
                        </OutlinedStadiumButton>
                        <StadiumButton variant='contained' disabled={isLoading || isUploading} type='submit'>
                            CONFIRM
                        </StadiumButton>
                    </DialogActions>
                </form>
            </ProjectDialog>
        </>
    );
};

export default MunicipalityMeterCreateDialog;
